const REDACTED_QUERY_KEYS = {
	access_token: true,
	code: true,
	id_token: true,
	refresh_token: true,
	state: true,
	token: true
};

let sequenceNumber = 0;
const startedAt = Date.now();
const LOGGER_GLOBAL_KEY = "__AMBIFI_WEB_LOGIN_SEQUENCE_LOGGER__";
const LOGGER_EVENTS_GLOBAL_KEY = "__AMBIFI_WEB_LOGIN_SEQUENCE_EVENTS__";
const LOGGER_VERSION = "2026-06-01-console-window-in-app-v2";

function markLoggerLoaded() {
	if (typeof window === "undefined") {
		return;
	}

	if (!Array.isArray(window[LOGGER_EVENTS_GLOBAL_KEY])) {
		window[LOGGER_EVENTS_GLOBAL_KEY] = [];
	}

	window[LOGGER_GLOBAL_KEY] = {
		version: LOGGER_VERSION,
		loadedAt: new Date(startedAt).toISOString(),
		getEvents: () => window[LOGGER_EVENTS_GLOBAL_KEY],
		getSequenceNumber: () => sequenceNumber
	};
}

function redactUrl(value) {
	if (typeof value !== "string" || value === "") {
		return value;
	}

	if (value.charAt(0) === "?" || value.charAt(0) === "#") {
		return value.replace(/([?&#](?:access_token|code|id_token|refresh_token|state|token)=)[^&#]+/gi, "$1[redacted]");
	}

	try {
		const url = new URL(value, typeof window !== "undefined" && window.location ? window.location.origin : "https://app.ambifi.com");
		url.searchParams.forEach((_paramValue, key) => {
			if (REDACTED_QUERY_KEYS[key]) {
				url.searchParams.set(key, "[redacted]");
			}
		});
		return `${url.pathname}${url.search}${url.hash}`;
	} catch (_err) {
		return value
			.replace(/([?&](?:access_token|code|id_token|refresh_token|state|token)=)[^&#]+/gi, "$1[redacted]");
	}
}

function summarizeLocation(location) {
	if (!location) {
		return null;
	}

	if (typeof location === "string") {
		return redactUrl(location);
	}

	return {
		pathname: location.pathname,
		search: redactUrl(location.search || ""),
		hash: redactUrl(location.hash || "")
	};
}

export function getCurrentLocationSummary() {
	if (typeof window === "undefined" || !window.location) {
		return null;
	}

	return summarizeLocation({
		pathname: window.location.pathname,
		search: window.location.search,
		hash: window.location.hash
	});
}

function postInAppConsoleEvent(event) {
	if (typeof window === "undefined") {
		return;
	}

	const payload = {
		method: "debugLog",
		label: event.label,
		meta: event.meta,
		source: "cloud-webview",
		sequenceNumber: event.sequenceNumber,
		elapsedMs: event.elapsedMs,
		timestamp: event.timestamp
	};

	try {
		if (window.ReactNativeWebView && typeof window.ReactNativeWebView.postMessage === "function") {
			window.ReactNativeWebView.postMessage(JSON.stringify(payload));
		} else if (window.webView && typeof window.webView.postMessage === "function") {
			window.webView.postMessage(JSON.stringify(payload));
		}
	} catch (err) {
		if (typeof console !== "undefined" && typeof console.warn === "function") {
			console.warn("Unable to post in-app console event", err);
		}
	}
}

function logSequenceEvent(label, meta = {}, options = {}) {
	sequenceNumber += 1;
	const elapsedMs = Date.now() - startedAt;
	const safeMeta = Object.assign({
		location: getCurrentLocationSummary()
	}, meta || {});
	const event = {
		sequenceNumber,
		elapsedMs,
		label,
		meta: safeMeta,
		timestamp: new Date().toISOString()
	};

	if (typeof window !== "undefined" && Array.isArray(window[LOGGER_EVENTS_GLOBAL_KEY])) {
		window[LOGGER_EVENTS_GLOBAL_KEY].push(event);
	}

	if (options.inAppConsole === true) {
		postInAppConsoleEvent(event);
	}

	if (typeof console !== "undefined") {
		const message = `[web-login-sequence #${sequenceNumber} +${elapsedMs}ms] ${label}`;
		if (typeof console.info === "function") {
			console.info(message, safeMeta);
		}
		if (typeof console.log === "function") {
			console.log(message, safeMeta);
		}
	}

	return event;
}

export function logWebLoginSequence(label, meta = {}) {
	return logSequenceEvent(label, meta);
}

export function logInAppConsole(label, meta = {}) {
	return logSequenceEvent(label, meta, {
		inAppConsole: true
	});
}

markLoggerLoaded();
logWebLoginSequence("logger loaded", {
	version: LOGGER_VERSION
});

export { summarizeLocation };
